import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';
import { IContactFormDataKeys } from '../../create-contact-form/create-contact-form.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { ITestContact } from '../../../../types/settingsDataModule';
import { countries } from '../../../../../utils/countriesList';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

/** Edit a test number. Fields the form no longer shows keep their saved values. */
@Component({
  selector: 'update-test-user-form',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    InputFieldComponent,
    ReactiveFormsModule,
    NgxMatInputTelComponent,
    CustomizableButtonComponent,
  ],
  templateUrl: './update-test-user-form.component.html',
  styleUrls: ['../../../../pages/organization/settings/settings-ui.css', './update-test-user-form.component.css'],
})
export class UpdateTestUserFormComponent implements OnChanges, OnDestroy {
  constructor(private builder: FormBuilder) {}
  @Output() onClose = new EventEmitter();
  @Output() fetchTestContacts = new EventEmitter();
  @Output() onSuccess = new EventEmitter<void>();
  @Input({ required: true }) openModal = false;
  @Input({ required: true }) selectedTestUser: ITestContact | null = null;

  settingsService = inject(SettingsService);

  submitted: boolean = false;
  isCreating: boolean = false;
  countries = countries;

  // A failed save stays in the modal until the next try.
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;
  private feedbackTimeoutId?: ReturnType<typeof setTimeout>;

  testContactForm = this.builder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    emailAddress: ['', [Validators.email]],
    primaryContactNumber: ['', Validators.required],
    // Kept, not shown: saved values go back unchanged.
    gender: [''],
    birthDate: [''],
    country: [''],
    zipCode: [''],
    city: [''],
  });

  getFormInput(name: IContactFormDataKeys) {
    return this.testContactForm.get(name);
  }

  get emailErrorMsg() {
    const email = this.getFormInput('emailAddress');
    if (email?.errors?.['email']) {
      return 'Enter a full email address, like name@company.com, or leave it empty.';
    }
    return '';
  }

  countryChanged(country: any) {
    // As before: only the dialling code follows the phone; the saved country stays.
    this.testContactForm.patchValue({
      zipCode: country?.dialCode ? `+${country.dialCode}` : '',
    });
  }

  handleSubmit() {
    this.submitted = true;
    this.showFeedback = false;
    if (this.testContactForm.valid) {
      this.isCreating = true;
      const value = this.testContactForm.value;
      this.settingsService
        .updateTestContact({
          ...(value as ITestContact),
          // PATCH ignores missing fields, so a cleared email is sent as '' to remove it.
          emailAddress: (value.emailAddress ?? '').trim(),
          gender: (value.gender || undefined) as any,
          birthDate: value.birthDate || (undefined as any),
          id: this.selectedTestUser?.id,
        })
        .subscribe({
          next: () => {
            this.isCreating = false;
            this.fetchTestContacts.emit();
            this.onSuccess.emit();
            this.onClose.emit();
          },
          error: (error) => {
            this.isCreating = false;
            const errorMessage =
              error?.error?.message ||
              error?.error?.detail ||
              error?.message ||
              'The changes were not saved. Check your connection and try again.';
            this.displayFeedback(errorMessage, 'error');
          },
          complete: () => {
            this.isCreating = false;
          },
        });
    }
  }

  displayFeedback(message: string, type: FeedbackType) {
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    this.feedbackTimeoutId = setTimeout(() => {
      this.showFeedback = false;
      this.feedbackTimeoutId = undefined;
    }, 10000);
  }

  ngOnDestroy() {
    if (this.feedbackTimeoutId) {
      clearTimeout(this.feedbackTimeoutId);
    }
  }

  ngOnChanges() {
    if (this.selectedTestUser) {
      this.submitted = false;
      this.showFeedback = false;
      this.testContactForm.patchValue({
        birthDate: this.selectedTestUser.birthDate,
        city: this.selectedTestUser.city,
        country: this.selectedTestUser.country,
        emailAddress: this.selectedTestUser.emailAddress,
        firstName: this.selectedTestUser.firstName,
        gender: this.selectedTestUser.gender,
        lastName: this.selectedTestUser.lastName,
        primaryContactNumber: this.selectedTestUser.primaryContactNumber,
        zipCode: this.selectedTestUser.zipCode,
      });
    }
  }
}
